import { useEffect, useRef } from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

const NAME_PATTERN = /^[a-z][a-z0-9_]+$/;

export const AgentNameField = ({
  onChange,
  rawErrors,
  formContext,
  schema,
}: FieldExtensionComponentProps<string>) => {
  const formValues = formContext?.formData as Record<string, any> | undefined;
  const agentType = formValues?.agent_type as string | undefined;
  const currentValue = (formValues?.agent_name || '') as string;

  const isA2a = agentType === 'a2a';
  const needsSuffix = isA2a && currentValue && !currentValue.endsWith('_a2a');
  const hasStaleA2aSuffix = !isA2a && !!currentValue && currentValue.endsWith('_a2a');
  const isValid = !currentValue || NAME_PATTERN.test(currentValue);
  const errorMessage = rawErrors?.[0];
  const prevIsA2a = useRef(isA2a);

  // When agent_type switches to a2a and name is already filled, append suffix
  useEffect(() => {
    if (isA2a && !prevIsA2a.current && currentValue && !currentValue.endsWith('_a2a')) {
      const base = currentValue.replace(/_+$/, '');
      if (base) {
        onChange(`${base}_a2a`);
      }
    }
    prevIsA2a.current = isA2a;
  }, [isA2a, currentValue, onChange]);

  const handleBlur = () => {
    if (needsSuffix) {
      const base = currentValue.replace(/_+$/, '');
      if (base) {
        onChange(`${base}_a2a`);
      }
    }
  };

  return (
    <FormControl
      margin="normal"
      error={(!isValid && !!currentValue) || hasStaleA2aSuffix || (rawErrors?.length > 0 && !rawErrors[0]?.startsWith('__no__error__'))}
      fullWidth
    >
      <InputLabel htmlFor="agent_name" shrink>
        {schema.title || 'Agent Name'}
      </InputLabel>
      <Input
        id="agent_name"
        value={currentValue}
        onChange={e => onChange(e.target?.value)}
        onBlur={handleBlur}
        placeholder="e.g. job_summarizer"
        autoFocus
      />
      {!isValid && currentValue && (
        <FormHelperText error>
          Must be snake_case: start with a lowercase letter, only lowercase letters, numbers, and underscores.
        </FormHelperText>
      )}
      {needsSuffix && (
        <FormHelperText>
          _a2a suffix will be added automatically when you leave this field.
        </FormHelperText>
      )}
      {hasStaleA2aSuffix && (
        <FormHelperText error>
          Agent type is not A2A — please remove the _a2a suffix from the name.
        </FormHelperText>
      )}
      {errorMessage && (
        <FormHelperText error>{errorMessage}</FormHelperText>
      )}
    </FormControl>
  );
};
